import { isRecord } from "../common/typeguards";
import type {
    ScreenRegionType as LensCoreScreenRegionType,
    ScreenRegionsInput as LensCoreScreenRegionsInput,
} from "../lens-core-module/generated-types";

/**
 * Defines the types of screen regions that can be configured for Lens rendering.
 *
 * - `roundButton`: Circular UI buttons (e.g., capture button)
 * - `topBar`: Top navigation or status bar area
 * - `keyboard`: Virtual keyboard area when shown
 * - `safeRender`: Safe area for Lens content to avoid UI overlap
 * - `captureExitButton`: Exit button for capture mode
 */
export type ScreenRegionType = "roundButton" | "topBar" | "keyboard" | "safeRender" | "captureExitButton";

/**
 * Represents a screen region with its normalized rectangular bounds.
 *
 * All coordinates are normalized values between 0.0 and 1.0 relative to the canvas size.
 * For example, { x: 0.5, y: 0.5, width: 0.5, height: 0.5 } represents right-bottom quarter of the canvas.
 */
export interface ScreenRegion {
    /**
     * Normalized x-coordinate of the region's top-left corner (0.0 to 1.0).
     */
    x: number;
    /**
     * Normalized y-coordinate of the region's top-left corner (0.0 to 1.0).
     */
    y: number;
    /**
     * Normalized width of the region (0.0 to 1.0).
     */
    width: number;
    /**
     * Normalized height of the region (0.0 to 1.0).
     */
    height: number;
}

/**
 * Configuration for screen regions used by Lenses.
 *
 * Screen regions help Lenses adapt their content placement based on the host
 * application's UI layout. All coordinates are normalized (0.0 to 1.0) relative
 * to the canvas dimensions.
 *
 * Only the regions specified in this object will be active - any previously set
 * regions not included will be automatically removed.
 *
 * @example
 * ```typescript
 * // Set initial safe rendering area
 * await session.setScreenRegions({
 *   safeRender: {
 *     x: 0.1,
 *     y: 0.1,
 *     width: 0.8,
 *     height: 0.8
 *   }
 * });
 *
 * // When keyboard appears, update both regions
 * await session.setScreenRegions({
 *   safeRender: {
 *     x: 0.1,
 *     y: 0.1,
 *     width: 0.8,
 *     height: 0.5
 *   },
 *   keyboard: {
 *     x: 0,
 *     y: 0.5,
 *     width: 1.0,
 *     height: 0.5
 *   }
 * });
 *
 * // When keyboard disappears, only specify safeRender
 * // (keyboard region will be automatically removed)
 * await session.setScreenRegions({
 *   safeRender: {
 *     x: 0.1,
 *     y: 0.1,
 *     width: 0.8,
 *     height: 0.8
 *   }
 * });
 * ```
 */
export type ScreenRegions = {
    [K in ScreenRegionType]?: ScreenRegion;
};

// Mapping between JavaScript-friendly API names and LensCore internal names
// This allows us to present a clean camelCase API while maintaining compatibility
// with the underlying LensCore system that uses snake_case naming
const REGION_TYPE_MAPPING: Record<ScreenRegionType, LensCoreScreenRegionType> = {
    roundButton: "round_button",
    topBar: "top_bar",
    keyboard: "keyboard",
    safeRender: "safe_render",
    captureExitButton: "capture_exit_button",
};

/**
 * Type guard to validate ScreenRegions input.
 *
 * - Ensures `value` is a record
 * - Ensures every key is a known ScreenRegionType (via REGION_TYPE_MAPPING)
 * - Ensures every value is a normalized rect in [0,1], non-negative size,
 *   and fits within the canvas (with tiny epsilon tolerance)
 */
export function isScreenRegions(value: unknown): value is ScreenRegions {
    if (!isRecord(value)) return false;

    for (const [key, rect] of Object.entries(value)) {
        if (!(key in REGION_TYPE_MAPPING)) return false;
        if (!isNormalizedRectLike(rect)) return false;
    }
    return true;
}

const EPS = 1e-6;

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function isNormalizedRectLike(obj: unknown): obj is ScreenRegion {
    if (!isRecord(obj)) return false;

    // Pull fields as unknowns first
    const x = (obj as any).x;
    const y = (obj as any).y;
    const width = (obj as any).width;
    const height = (obj as any).height;

    // Shape + type checks
    if (![x, y, width, height].every(isFiniteNumber)) return false;

    // Non-negative size
    if (width < 0 || height < 0) return false;

    // Bounds with small epsilon tolerance
    const x2 = x + width;
    const y2 = y + height;

    if (x < -EPS || y < -EPS) return false;
    if (x > 1 + EPS || y > 1 + EPS) return false;
    if (x2 > 1 + EPS || y2 > 1 + EPS) return false;

    return true;
}

/**
 * Handles the complete logic for updating screen regions.
 *
 * This function implements the "current state" pattern where only the regions
 * specified in newRegions will be active, and any previously set regions
 * not included will be automatically removed.
 *
 * @param newRegions - The new screen regions to set (public API format)
 * @param currentRegions - The currently active screen regions (for tracking)
 * @returns Object containing LensCore input and updated current regions
 */
export function prepareScreenRegionsUpdate(newRegions: ScreenRegions): LensCoreScreenRegionsInput["regions"] {
    const mappedRegions: LensCoreScreenRegionsInput["regions"] = {};
    for (const [regionType, region] of Object.entries(newRegions)) {
        const lensCoreRegionType = REGION_TYPE_MAPPING[regionType as ScreenRegionType];
        // LensCore expects regions wrapped in a normalizedRect property
        mappedRegions[lensCoreRegionType] = {
            normalizedRect: {
                origin: {
                    x: region.x,
                    y: region.y,
                },
                size: {
                    width: region.width,
                    height: region.height,
                },
            },
        };
    }
    return mappedRegions;
}
